import {
  trapFocus,
  removeTrapFocus,
} from '@shopify/theme-a11y';
import EventHandler from '@pixelunion/events';
import { transition } from '@pixelunion/animations';
import ScrollLock from '../helpers/ScrollLock';

export default class PredictiveSearch {
  constructor() {
    if (document.body.dataset.predictiveSearch === 'false') return;
    this.events = new EventHandler();
    this.searchOverlay = document.querySelector('[data-search-overlay]');
    this.search = document.querySelector('[data-search]');
    this.searchForm = document.querySelector('[data-search-form]');
    this.resultsWrapper = document.querySelector('[data-results-wrapper]');
    this.searchMessage = document.querySelector('[data-predictive-search-message]');
    this.searchType = document.querySelector('[data-search-type]').value;
    this.searchTerms = document.querySelector('[data-search-terms]');
    this.productSearchLink = document.querySelector('[data-product-search-link]');
    this.openSearchButtons = document.querySelectorAll('[data-search-open]');
    this.closeSearchButton = document.querySelector('[data-close-search]');
    this.productSearchResults = document.querySelector('[data-product-search-results]');

    this.trigger = null;
    this.timer = null;
    this.getResults = this.getResults.bind(this);

    this.animateSearchOverlay = transition({ el: this.searchOverlay, state: 'closed' });
    this.animateSearch = transition({ el: this.search, state: 'closed' });

    if (document.querySelector('[data-side-search-results]')) {
      this.sideSearchLink = document.querySelector('[data-side-search-link]');
      this.sideSearchResults = document.querySelector('[data-side-search-results]');
    }

    this.openSearchButtons.forEach(openButton => {
      this.events.register(openButton, 'click', e => {
        e.preventDefault();
        this.openSearch();
        this.trigger = openButton;
      });
    });

    this.events.register(this.searchOverlay, 'click', e => {
      if (e.target.classList.contains('predictive-search__overlay')) {
        this.closeSearch();
      }
    });

    this.events.register(this.closeSearchButton, 'click', e => {
      e.preventDefault();
      this.closeSearch();
    });

    this._closeEsc = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.closeSearch();
      }
    };
  }

  openSearch() {
    this.searchOverlay.classList.add('predictive-search__overlay--active');

    this.animateSearchOverlay.animateTo('open');
    this.animateSearch.animateTo('open').then(() => {
      trapFocus(this.search);
      this.searchTerms.focus();
    });

    this.events.register(this.searchTerms, 'keyup', e => {
      clearTimeout(this.timer);
      if (this.searchTerms.value.length >= 2 && e.keyCode !== 9) { // Check if word greater than 2 letters and we are not trying to tab
        this.timer = setTimeout(this.getResults, 300);
      } else if (this.searchTerms.value === '') {
        this.clearResults();
      }
    });

    this.closeEscEvent = this.events.register(window, 'keydown', e => this._closeEsc(e));
  }

  closeSearch() {
    this.events.unregister(this.searchOverlay);
    this.events.unregister(this.searchTerms);
    this.events.unregister(this.closeEscEvent);

    this.clearResults();

    this.resultsWrapper.classList.remove('predictive-search__results--show');

    this.animateSearch.animateTo('closed');
    this.animateSearchOverlay.animateTo('closed')
      .then(() => {
        this.searchOverlay.classList.remove('predictive-search__overlay--active');
        this.searchTerms.value = '';
        ScrollLock.unlock();
        removeTrapFocus(this.search);
        this.trigger.focus();
      });
  }

  getResults() {
    fetch(`${Shopify.routes.search_url}/suggest.json?q=${this.searchTerms.value}&resources[type]=${this.searchType}&resources[limit]=5&resources[options][unavailable_products]=last&resources[options][fields]=title,body,variants.title,vendor,product_type,tag`)
      .then(response => response.json())
      .then(suggestions => {
        const { results } = suggestions.resources;
        const { products, articles, pages } = results;

        this.clearResults();
        this.resultsWrapper.classList.add('predictive-search__results--show');
        ScrollLock.lock(this.resultsWrapper);

        if (products?.length > 0 || articles?.length > 0 || pages?.length > 0) {
          this.displayResults(products, articles, pages);
          this.setLinks();
        } else {
          this.noResults();
        }
      });
  }

  clearResults() {
    this.resultsWrapper.classList.remove('predictive-search__results--show');

    while (this.searchMessage.firstChild) {
      this.searchMessage.removeChild(this.searchMessage.firstChild);
    }

    while (this.productSearchResults.firstChild) {
      this.productSearchResults.removeChild(this.productSearchResults.firstChild);
    }

    if (this.sideSearchResults) {
      while (this.sideSearchResults.firstChild) {
        this.sideSearchResults.removeChild(this.sideSearchResults.firstChild);
      }
    }
  }

  noResults() {
    this.resultsWrapper.classList.add('predictive-search__results-no-results');
    const noResultsMessage = document.createElement('p');
    noResultsMessage.textContent = `${Shopify.translation.your_search} "${this.searchTerms.value}" ${Shopify.translation.no_match}`;
    this.searchMessage.appendChild(noResultsMessage);
    this.searchMessage.classList.add('predictive-search__message--show');

    removeTrapFocus(this.search);
    trapFocus(this.search);
    this.searchTerms.focus();
  }

  displayResults(products, articles, pages) {
    let productLimit;

    this.resultsWrapper.classList.remove('predictive-search__results-no-results');
    this.searchMessage.classList.remove('predictive-search__message--show');

    if (this.resultsWrapper.classList.contains('predictive-search__results--everything')) {
      productLimit = 4;
    } else {
      productLimit = 5;
    }

    this.displayProducts(products, productLimit);

    if (this.sideSearchResults) {
      if (articles.length > 0 || pages.length) {
        this.displayArticles(articles);
        this.displayPages(pages);

        this.sideSearchLink.style.display = 'block';
      } else {
        const noResultsMessage = document.createElement('li');

        noResultsMessage.textContent = Shopify.translation.no_results;

        this.sideSearchResults.appendChild(noResultsMessage);
        this.sideSearchLink.style.display = 'none';
      }
    }

    removeTrapFocus(this.search);
    trapFocus(this.search);
    this.searchTerms.focus();
  }

  displayProducts(products, productLimit) {
    if (products.length > 0) {
      products.slice(-productLimit).forEach(product => { // using slice to ensure we limit the amount of results being looped
        const productResult = document.createElement('div');
        const { title, image, url } = product;

        productResult.classList.add('search-results__product');
        productResult.innerHTML = `
          <a tabindex="0" class="search-results__product-wrapper" href="${url}">
            <img src="${this.imageDimension(image, '_600x')}" />
            <div>
              <span class="product-thumbnail__title">${title}</span>
              ${this.displayPrice(product)}
            </div>
          </a>
        `;

        this.productSearchResults.appendChild(productResult);
      });
      this.productSearchLink.style.display = 'block';
    } else {
      const noResultsMessage = document.createElement('p');

      noResultsMessage.classList.add('no-results');
      noResultsMessage.textContent = Shopify.translation.no_results;

      this.productSearchResults.appendChild(noResultsMessage);
      this.productSearchLink.style.display = 'none';
    }
  }

  displayArticles(articles) {
    articles.slice(-3).forEach(article => { // using slice to ensure we limit the amount of results being looped
      const articleResult = document.createElement('li');
      const { title, url } = article;

      articleResult.classList.add('search-results__side');
      articleResult.innerHTML = `
        <a tabindex="0" href="${url}">
          ${title}
        </a>
      `;

      this.sideSearchResults.appendChild(articleResult);
    });
  }

  displayPages(pages) {
    pages.slice(-3).forEach(page => { // using slice to ensure we limit the amount of results being looped
      const pageResult = document.createElement('li');
      const { title, url } = page;

      pageResult.classList.add('search-results__side');
      pageResult.innerHTML = `
        <a tabindex="0" href="${url}">
          ${title}
        </a>
      `;

      this.sideSearchResults.appendChild(pageResult);
    });
  }

  setLinks() {
    this.productSearchLink.setAttribute('href', `${Shopify.routes.search_url}?type=product&q=${this.searchTerms.value}`);
    if (this.sideSearchLink) {
      this.sideSearchLink.setAttribute('href', `${Shopify.routes.search_url}?type=page%2Carticle&q=${this.searchTerms.value}`);
    }
  }

  displayPrice(product) {
    let price;
    if (product.available === true) {
      if (product.compare_at_price_max > product.price_max || product.compare_at_price_min > product.price_min ) { // eslint-disable-line
        price = `<span class="product-thumbnail__price product__sale-price">${this.formatPrice(product.price)} <span class="product__compare-price">${this.formatPrice(product.compare_at_price_max)}</span></span>`;
      } else if (product.price > 0 && product.price_min !== product.price_max) {
        price = `<span class="product-thumbnail__price">${Shopify.translation.product_from} ${this.formatPrice(product.price)}</span>`;
      } else if (product.price > 0 && product.price_min === product.price_max) {
        price = `<span class="product-thumbnail__price">${this.formatPrice(product.price)}</span>`;
      } else {
        price = `<span class="product-thumbnail__price">${Shopify.theme_settings.free_text}</span>`;
      }
    } else {
      price = `<span class="product-thumbnail__price">${Shopify.translation.product_sold_out}</span>`;
    }

    return price;
  }

  formatPrice(price) {
    let formattedPrice;

    if (Currency.display_format === 'money_with_currency_format') {
      formattedPrice = `<span class="money">${Currency.symbol}${price} ${Currency.iso_code}</span>`;
    } else {
      formattedPrice = `<span class="money">${Currency.symbol}${price}</span>`;
    }

    return formattedPrice;
  }

  imageDimension(url, size) {
    if (url !== null) {
      const insertPosition = url.lastIndexOf('.');
      return url.substring(0, insertPosition) + size + url.substring(insertPosition);
    }

    return '';
  }
}
